import DeleteIcon from '@mui/icons-material/Delete'
import { Stack, IconButton, Button, Tooltip } from '@mui/material'
import { EditButton, ShowButton } from '@refinedev/mui'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useDeleteScoutMutation } from '../../../api'
import { DeleteScoutDialog } from '../../../DeleteScoutDialog'
import type { Scout } from '../../../types.ts'

interface ScoutActionButtonsProps {
  record: Scout
  canDelete?: boolean
  hideText?: boolean
  size?: 'small' | 'medium' | 'large'
}

export const ScoutActionButtons: React.FC<ScoutActionButtonsProps> = ({
  record,
  canDelete = false,
  hideText = true,
  size = 'small',
}) => {
  const navigate = useNavigate()
  const deleteScoutMutation = useDeleteScoutMutation()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(`/scouts/edit/${record.uuid}`)
  }

  const handleShow = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(`/scouts/show/${record.uuid}`)
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    try {
      await deleteScoutMutation.mutateAsync(record.uuid)
    } catch (error) {
      console.error('Failed to delete scout:', error)
    }
    setDeleteDialogOpen(false)
  }

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
  }

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <EditButton
          hideText={hideText}
          size={size}
          recordItemId={record.uuid}
          onClick={handleEdit}
        />
        <ShowButton
          hideText={hideText}
          size={size}
          recordItemId={record.uuid}
          onClick={handleShow}
        />
        {canDelete &&
          (hideText ? (
            <Tooltip title="Delete">
              <IconButton
                size={size}
                onClick={handleDeleteClick}
                disabled={deleteScoutMutation.isPending}
                color="error"
              >
                <DeleteIcon fontSize={size} />
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              size={size}
              onClick={handleDeleteClick}
              disabled={deleteScoutMutation.isPending}
              color="error"
              startIcon={<DeleteIcon />}
              sx={{
                minWidth: 'auto',
                textTransform: 'none',
              }}
            >
              Delete
            </Button>
          ))}
      </Stack>

      <DeleteScoutDialog
        open={deleteDialogOpen}
        scout={record}
        isDeleting={deleteScoutMutation.isPending}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  )
}
